import type { MatterDocumentSubItem } from "@/lib/types";
import { getMatterContactConnections } from "@/lib/utils/matters/matter-contact-connection-party-roles";
import { getVehicleTemplateFieldValue } from "./vehicleTemplateValues";
import { getInsuranceTemplateFieldValue } from "./insuranceTemplateValues";
import {
  buildSettlementNegotiationContactConnections,
  isSettlementNegotiationContactConnection,
} from "./settlementNegotiationTemplateContacts";
import { scopeMatterToSubItem } from "./scopeMatterToSubItem";
import {
  buildTemplateDictionaryOptions,
  buildIncidentReportSourceConnections,
  buildMedicalTreatmentProviderConnections,
  type TemplateDictionaryOptionsMap,
} from "./templateDictionaryOptions";

type TemplateOptionsInput = Parameters<
  typeof buildTemplateDictionaryOptions
>[0];

/** Keep every choice available, limiting only defaults to the source object. */
export const buildContextualTemplateDictionaryOptions = (
  input: TemplateOptionsInput,
  subItem?: MatterDocumentSubItem,
) => {
  const { matter, vehicleOwnerContacts = [] } = input;
  const clickedVehicle =
    subItem?.type === "vehicle"
      ? matter.vehicles.find((vehicle) => vehicle.id === subItem.id)
      : undefined;
  const includeClickedVehicle = (party: "client" | "defendant") => {
    const vehicles =
      party === "client" ? input.clientVehicles : input.defendantVehicles;
    return clickedVehicle &&
      subItem?.vehicleParty === party &&
      !vehicles.some((vehicle) => vehicle.id === clickedVehicle.id)
      ? [clickedVehicle, ...vehicles]
      : vehicles;
  };
  const clientVehicles = includeClickedVehicle("client");
  const defendantVehicles = includeClickedVehicle("defendant");
  const templateDictionaryOptions = buildTemplateDictionaryOptions({
    ...input,
    clientVehicles,
    defendantVehicles,
  });
  if (!subItem) {
    return {
      clientVehicles,
      defendantVehicles,
      templateDictionaryOptions,
      defaultTemplateDictionaryOptions: templateDictionaryOptions,
    };
  }

  const scopedMatter = scopeMatterToSubItem(matter, subItem);
  const scopedVehicles = (party: "client" | "defendant") => {
    const vehicles = party === "client" ? clientVehicles : defendantVehicles;
    return subItem.type === "vehicle"
      ? vehicles.filter(
          (vehicle) =>
            subItem.vehicleParty === party && vehicle.id === subItem.id,
        )
      : vehicles;
  };
  const defaultTemplateDictionaryOptions = buildTemplateDictionaryOptions({
    ...input,
    matter: scopedMatter,
    contacts: [
      ...getMatterContactConnections(scopedMatter)
        .filter(
          (connection) =>
            !connection.incidentReportId &&
            !isSettlementNegotiationContactConnection(connection) &&
            connection.label !== "Provider",
        )
        .map((connection) => ({
          ...connection,
          contact:
            vehicleOwnerContacts.find(
              (contact) => contact.id === connection.contactId,
            ) ?? connection.contact,
        })),
      ...buildIncidentReportSourceConnections(
        scopedMatter.incidentReports,
        vehicleOwnerContacts,
      ),
      ...buildMedicalTreatmentProviderConnections(
        scopedMatter,
        vehicleOwnerContacts,
      ),
      ...buildSettlementNegotiationContactConnections(
        scopedMatter,
        vehicleOwnerContacts,
        subItem,
      ),
    ],
    clientInsurance: input.clientInsurance.filter((insurance) =>
      scopedMatter.insurances.some((item) => item.id === insurance.id),
    ),
    defendantInsurance: input.defendantInsurance.filter((insurance) =>
      scopedMatter.insurances.some((item) => item.id === insurance.id),
    ),
    clientVehicles: scopedVehicles("client"),
    defendantVehicles: scopedVehicles("defendant"),
  });
  if (clickedVehicle && subItem.vehicleParty) {
    const prefix =
      subItem.vehicleParty === "client" ? "clientVehicle" : "defendantVehicle";
    for (const variable of Object.keys(defaultTemplateDictionaryOptions)) {
      if (!variable.startsWith(prefix)) continue;
      const value = getVehicleTemplateFieldValue(
        clickedVehicle,
        variable,
        prefix,
        templateDictionaryOptions,
      );
      defaultTemplateDictionaryOptions[variable] = value
        ? [{ value, label: value, context: "", vehicleId: clickedVehicle.id }]
        : [];
    }
  }
  if (subItem.type === "insurance") {
    for (const prefix of ["clientInsurance", "defendantInsurance"] as const) {
      const insurance = input[prefix].find((item) => item.id === subItem.id);
      for (const variable of Object.keys(defaultTemplateDictionaryOptions)) {
        if (!variable.startsWith(prefix)) continue;
        const value = insurance
          ? getInsuranceTemplateFieldValue(insurance, variable, prefix)
          : undefined;
        defaultTemplateDictionaryOptions[variable] = value
          ? [{ value, label: value, context: "" }]
          : [];
      }
    }
  }
  return {
    clientVehicles,
    defendantVehicles,
    templateDictionaryOptions,
    defaultTemplateDictionaryOptions,
  };
};

/** Aggregates always describe the whole matter, including in object flows. */
export const getTemplateDefaultOptions = (
  baseVariable: string,
  options: TemplateDictionaryOptionsMap,
  defaults: TemplateDictionaryOptionsMap,
) => (/^(all|total|netClientIssued$)/.test(baseVariable) ? options : defaults);
